export type NewBadgeState = {
  firstSeenAt: string | null;
  dismissedAt: string | null;
};

type LegacyNewBadgeState = {
  firstSeenAt?: string | null;
  dismissed?: boolean;
  dismissedAt?: string | null;
};

// This equates to 30 days in milliseconds.
export const NEW_BADGE_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

export const createNewBadgeState = (now: Date = new Date()): NewBadgeState => ({
  firstSeenAt: now.toISOString(),
  dismissedAt: null,
});

export const markNewBadgeSeen = (
  currentState: LegacyNewBadgeState | null,
  now: Date = new Date(),
): NewBadgeState => {
  const normalizedState = normalizeNewBadgeState(currentState, now);

  if (normalizedState) {
    return normalizedState;
  }

  return createNewBadgeState(now);
};

export const dismissNewBadgeState = (
  currentState: LegacyNewBadgeState | null,
  now: Date = new Date(),
): NewBadgeState => {
  const normalizedState = normalizeNewBadgeState(currentState, now);

  return {
    firstSeenAt: normalizedState?.firstSeenAt ?? now.toISOString(),
    dismissedAt: normalizedState?.dismissedAt ?? now.toISOString(),
  };
};

export const normalizeNewBadgeState = (
  currentState: LegacyNewBadgeState | null,
  now: Date = new Date(),
): NewBadgeState | null => {
  if (!currentState) {
    return null;
  }

  const firstSeenAt = currentState.firstSeenAt ?? null;
  const dismissedAt =
    currentState.dismissedAt ??
    (currentState.dismissed ? (firstSeenAt ?? now.toISOString()) : null);

  if (!firstSeenAt && !dismissedAt) {
    return null;
  }

  return {
    firstSeenAt,
    dismissedAt,
  };
};

export const shouldShowNewBadge = (
  currentState: LegacyNewBadgeState | null,
  now: number = Date.now(),
) => {
  const normalizedState = normalizeNewBadgeState(currentState);

  if (!normalizedState || normalizedState.dismissedAt) {
    return false;
  }

  const firstSeenAt = normalizedState.firstSeenAt
    ? new Date(normalizedState.firstSeenAt).getTime()
    : Number.NaN;

  if (Number.isNaN(firstSeenAt)) {
    return false;
  }

  return now - firstSeenAt < NEW_BADGE_DURATION_MS;
};
